import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Linkedin } from 'lucide-react';
import { WipeLink } from '../components/Wipe';
import { MaskedLines, FadeUp, Eyebrow } from '../components/Reveal';
import Footer from '../components/Footer';

const Author = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const motifY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);

  return (
    <div className="bg-paper" data-testid="author-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden px-5 md:px-10 lg:px-24 pt-36 pb-16" data-testid="author-hero">
        <motion.div style={{ y: motifY }} className="absolute -right-28 md:-right-10 top-[18vh] w-[60vw] md:w-[30vw] opacity-90 pointer-events-none select-none">
          <motion.img
            src="/assets/motifs/Group-20.svg"
            alt=""
            className="w-full animate-spin-slow"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.4 }}
          />
        </motion.div>
        <FadeUp y={14}>
          <p className="text-support text-xs md:text-sm font-semibold uppercase tracking-[0.35em]">
            The Author
          </p>
        </FadeUp>
        <MaskedLines
          onLoad
          delay={0.25}
          lines={['Aditya', 'Ranka.']}
          className="relative z-10 mt-4 text-[18vw] md:text-[11vw] font-light tracking-tight leading-[0.9] text-ink"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-6 text-ink/70 text-base md:text-lg max-w-xl leading-relaxed"
        >
          Industrial designer. System thinker. A businessman by upbringing, a designer by choice.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-wrap gap-4"
        >
          <a href="mailto:contact@adityarankaa.com" data-testid="author-mail-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">Mail</a>
          <a href="tel:+919826025454" data-testid="author-phone-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">[IN] +91 98260 25454</a>
          <a href="https://www.behance.net/adityarka" target="_blank" rel="noopener noreferrer" data-testid="author-behance-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">Behance</a>
          <a href="https://www.linkedin.com/in/adityarka" target="_blank" rel="noopener noreferrer" data-testid="author-linkedin-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">LinkedIn</a>
        </motion.div>
      </section>

      {/* STORY */}
      <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36 border-t border-ink/10" data-testid="author-story">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Eyebrow color="#B50A00">His Side of the Story</Eyebrow>
            <MaskedLines
              lines={['Fluent in business,', 'before design.']}
              className="mt-10 text-4xl md:text-6xl font-light tracking-tight leading-[1.05]"
            />
          </div>
          <FadeUp delay={0.15} className="lg:col-span-7 space-y-6">
            <p className="text-lg md:text-2xl font-normal leading-relaxed text-ink/85">
              "Coming from a background of Marwaris and lalas, I have been fed the whole crux of
              being a businessman and an entrepreneur. I think I understood the language of
              business more than the language of design when I started. But now, I know how to
              use that language for designing."
            </p>
            <p className="text-base md:text-lg text-ink/70 leading-relaxed">
              A semester 8 student of Industrial Design at UID Gandhinagar, an almost-there
              industrial designer and a wanna-be entrepreneur. A keen backer of design as a
              management tool: design gives you the escape to look at systems, strategies and
              management with a lens no other learning could teach. The endeavour is to grow
              into a strategic system designer-entrepreneur who runs his firm like a
              businessman, in the smartest and coolest ways.
            </p>
            <p className="text-base md:text-lg text-ink/70 leading-relaxed">
              He looks for insights everywhere, in every product, and builds personal systems
              and solutions in his free time, designing strategies around them.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* PROJECT DETAILS */}
      <section className="px-5 md:px-10 lg:px-24 pb-24 md:pb-36" data-testid="author-project">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/15 border border-ink/15">
          {[
            ['Project', 'Designing for the Next Phase of India\'s Offline Retail'],
            ['Programme', 'Bachelors of Design (Hons.), Product Design'],
            ['Institute', 'Unitedworld Institute of Design, Karnavati University, Gandhinagar'],
            ['Year', '2026 · Vol. 1 of 1'],
          ].map(([k, v], i) => (
            <FadeUp key={i} delay={i * 0.06} className="bg-paper p-7 md:p-9">
              <p className="text-[11px] uppercase tracking-[0.25em] text-support">{k}</p>
              <p className="mt-4 text-base md:text-lg font-medium leading-snug">{v}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* MENTOR */}
      <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="author-mentor">
        <Eyebrow color="#F9526B">Academic Guide</Eyebrow>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <MaskedLines
              lines={['Prof. Shhyam', 'Prajapati.']}
              className="text-4xl md:text-7xl font-light tracking-tight leading-[1.02]"
            />
            <FadeUp delay={0.15}>
              <p className="mt-6 text-xs uppercase tracking-[0.3em] text-paper/50">
                Ex-Dean, Department of Industrial Design, UID · Karnavati University
              </p>
              <a
                href="https://www.linkedin.com/in/shhyamprajapati/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Shhyam Prajapati on LinkedIn"
                data-testid="mentor-linkedin-link"
                className="mt-5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-paper/25 text-paper/60 transition-colors duration-300 hover:bg-paper hover:text-brand-black hover:border-paper"
              >
                <Linkedin size={14} />
              </a>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-6 flex flex-col justify-end">
            <p className="text-base md:text-lg text-paper/70 leading-relaxed">
              Design practitioner, consultant and academician with 18+ years across product
              design, design academics and research-based design, including building the design
              department at Bajaj Electricals and over 20 IPR filings from his industry years,
              and 120+ IPR filings from the department he leads. PhD and Master's in Design
              from IIT Guwahati.
            </p>
            <p className="mt-6 text-sm md:text-base text-paper/50 leading-relaxed">
              "Your feedback did not just improve the project, it refined the way I approach
              problems.", Aditya Ranka
            </p>
          </FadeUp>
        </div>
      </section>

      {/* BACK HOME */}
      <WipeLink
        to="/"
        data-testid="back-to-book"
        className="group relative block overflow-hidden bg-brand-red px-5 md:px-10 lg:px-24 py-24 md:py-32"
      >
        <div className="bandhani-dot absolute inset-0 text-paper/[0.06] pointer-events-none" />
        <p className="relative text-paper/60 text-xs uppercase tracking-[0.35em]">Return to</p>
        <div className="relative mt-4 flex items-end justify-between gap-6">
          <span className="text-paper text-5xl md:text-8xl font-light tracking-tight leading-none transition-transform duration-500 group-hover:translate-x-4">
            The Book
          </span>
          <span className="text-paper text-4xl md:text-6xl transition-transform duration-500 group-hover:translate-x-3 group-hover:-translate-y-1">→</span>
        </div>
      </WipeLink>
      <Footer />
    </div>
  );
};

export default Author;
